// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Re-simulating a trace (M3-39, FR-034, 10 §3).
//
// > "The server re-simulates a submitted replay before a score is admitted to any leaderboard."
//
// One function, and it is the same one on both sides of the exchange. The client uses it to
// **resume** an interrupted game — a resumable game is just a replay that has not finished yet,
// which is the whole benefit of a deterministic core and the reason there is no snapshot format
// here. The server (M4) uses it to decide whether a claimed score is real. A ghost replay (M5)
// uses it to play a stranger's game back frame by frame.
//
// **A snapshot would have been the obvious thing and it is the wrong thing.** Saving the ball
// positions, the rule variables, the timers, the lamp phases and the RNG state means writing a
// second description of the simulation, keeping it in step with the first for ever, and
// discovering it has drifted only when somebody's resumed game scores differently from the one
// they left. Re-running the trace has one description and cannot drift: 600,000 steps of
// arithmetic with no rendering takes well under a second, which is cheaper than the code to
// avoid it.
//
// **The order of operations here is the order in `PlaySession.frame`, exactly.** Input is applied
// at a frame boundary and then the driver runs whole steps; so a replay applies every event
// stamped with the current step and then advances one step. Getting that inverted — advancing
// first — puts every action one step late, which is invisible in a short game and is the
// difference between a passed and a failed skill shot in a long one.

import type { Game } from '../game/Game'
import { CHECKPOINT_STEPS, checkpointOf, traceProblem } from './trace'
import type { ReplayTrace, TraceCheckpoint } from './trace'

export interface ReplayOptions {
  /**
   * Resume from a game that is already at this step, instead of from a fresh one.
   *
   * How a replay longer than one Edge Function invocation is validated (M4-28, M4-28a). The
   * measured cost is about 2.8 µs a step and the platform allows roughly two seconds of CPU, so a
   * single invocation manages around 620,000 steps — a ten-minute game with nothing to spare, and
   * a two-hour one not at all. The game handed in must have been restored from a snapshot taken
   * after exactly this many steps.
   */
  readonly fromStep?: number
  /**
   * The first event the previous chunk had **not** applied, from its `nextEvent`.
   *
   * Carried rather than inferred, and that is the whole of the correction. `fromStep` counts calls
   * to `advance()`; `TraceEvent.step` is the *world's* step index, and `Game.advance()` only steps
   * the world while a ball is in play. Attract, bonus and game-over advance the game without
   * advancing the world, so the two numbers drift apart — and a resumed chunk that skipped events
   * by comparing them to `fromStep` skipped the wrong ones and scored a different game. Found by
   * chunking one real replay at 700 steps and at 1,000 and getting two different answers.
   */
  readonly fromEvent?: number
  /** Stop here instead of at the end. Used to scrub a ghost replay to a point (M5). */
  readonly untilStep?: number
  /**
   * Compare the checkpoints as they are reached, rather than only the final score.
   *
   * Off by default because a *resume* has nothing to compare against — it is re-running its own
   * trace, and any disagreement would be a bug in this build rather than a claim to check.
   */
  readonly verify?: boolean
  /** Live resume only: apply saved inputs at the final step without advancing.
   * Defaults off so validation and intermediate replay checkpoints retain their boundary. */
  readonly applyFinalInputs?: boolean
}

export interface ReplayResult {
  /** Why the trace could not be run at all, or nothing. */
  readonly refused?: string
  readonly steps: number
  readonly score: number
  readonly checkpoints: readonly TraceCheckpoint[]
  /** The index of the first event not yet applied, for the next chunk to carry back in. */
  readonly nextEvent: number
  /**
   * The first step whose checkpoint disagreed, or −1.
   *
   * This is 10 §3's `divergent` outcome, and it is deliberately reported separately from a score
   * mismatch: a run that reaches the right score by a different route has not been caught
   * cheating, it has caught **us** — cross-runtime determinism has slipped (PH-088), and the
   * honest thing to do with it is flag it for review rather than reject a player's game.
   */
  readonly divergedAt: number
}

/**
 * Play a trace into a game that has not been started yet.
 *
 * The game must be freshly constructed with the trace's seed and player count: `startGame` is
 * the trace's own first action, because "the launch button was pressed in attract mode" is how a
 * game begins and a replay reproduces what the player did rather than what the host did for them.
 */
export function replayTrace(game: Game, trace: ReplayTrace, options: ReplayOptions = {}): ReplayResult {
  const refused = traceProblem(trace)
  if (refused !== undefined) {
    return { refused, steps: 0, score: 0, checkpoints: [], divergedAt: -1, nextEvent: 0 }
  }

  const from = options.fromStep ?? 0
  const until = options.untilStep === undefined ? trace.steps : Math.min(options.untilStep, trace.steps)
  const checkpoints: TraceCheckpoint[] = []
  const expected = new Map<number, number>()
  if (options.verify === true) for (const c of trace.checkpoints) expected.set(c.step, c.hash)
  let divergedAt = -1

  // Where the previous chunk stopped reading the input. Taken from the caller rather than worked
  // out from `from`, because `from` counts `advance()` calls and an event's step counts *world*
  // steps, and those two only agree while a ball is in play.
  let next = options.fromEvent ?? 0
  let steps = from
  while (steps < until) {
    const step = game.step
    // Every event on this step, before the step runs. Actions recorded on a step the simulation
    // has already passed — which a malformed trace can contain — are applied here rather than
    // dropped, so a validator's answer never depends on how far behind the trace is.
    while (next < trace.events.length && trace.events[next]!.step <= step) {
      apply(game, trace.events[next]!.action)
      next++
    }

    game.advance()
    steps++

    // Hashed on the steps the *trace* names, and on the regular boundary otherwise. Comparing a
    // claim means standing where the claim was made: a validator that hashed on its own schedule
    // would produce a list with nothing to check against, however correct each entry was.
    const claim = expected.get(game.step)
    if (claim === undefined && game.step % CHECKPOINT_STEPS !== 0) continue
    const mark = checkpointOf(game)
    checkpoints.push(mark)
    if (divergedAt < 0 && claim !== undefined && claim !== mark.hash) divergedAt = game.step
  }

  if (options.applyFinalInputs === true && until === trace.steps) {
    while (next < trace.events.length && trace.events[next]!.step <= game.step) {
      apply(game, trace.events[next]!.action)
      next++
    }
  }

  return {
    steps,
    score: game.players[0]?.score ?? 0,
    checkpoints,
    divergedAt,
    nextEvent: next,
  }
}

function apply(game: Game, action: ReplayTrace['events'][number]['action']): void {
  switch (action.kind) {
    case 'flipper':
      game.pressButton(action.side, action.down)
      return
    case 'launch':
      game.pressLaunchButton(action.power)
      return
    case 'nudge':
      // Into the machine frame, never into a ball (PH-070) — the same call the live path makes.
      game.world.nudge(action.ix, action.iy)
      return
    case 'skip':
      game.initialsSkip()
      return
    case 'credit':
      game.addCredit()
      return
  }
}
